/**
 * 独自 Extension / Node
 *
 * 純ロジック (段数と px の変換・クランプ) は state 側にあり、ここは登録だけを担う
 */
import { Extension, Node, mergeAttributes } from '@tiptap/core';
import { clampIndent, indentToMarginLeft, marginLeftToIndent } from './state';

/** BlockLayout の対象ノード。段落と見出しに同じブロック属性を持たせる */
interface BlockLayoutOptions {
  types: string[];
}

// 追加するコマンドを型に載せる。無いと chain().setBlockTextAlign(...) が型エラーになる
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    blockLayout: {
      /** 選択中のブロックの text-align を設定する */
      setBlockTextAlign: (align: string) => ReturnType;
      /** 選択中のブロックの line-height を設定する */
      setBlockLineHeight: (lineHeight: string) => ReturnType;
      /** 選択中のブロックのインデント段数を増減する */
      changeBlockIndent: (delta: number) => ReturnType;
    };
  }
}

/**
 * paragraph / heading に textAlign・indent・lineHeight を持たせる
 * 揃えや行間はインライン span ではなくブロック自身の style でないと効かない
 */
export const BlockLayout = Extension.create<BlockLayoutOptions>({
  name: 'blockLayout',
  addOptions() {
    return { types: ['paragraph', 'heading'] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textAlign: {
            default: null,
            parseHTML: (el) => el.style.textAlign || null,
            renderHTML: (attrs) => (attrs.textAlign ? { style: `text-align: ${attrs.textAlign}` } : {}),
          },
          indent: {
            default: 0,
            parseHTML: (el) => marginLeftToIndent(el.style.marginLeft),
            renderHTML: (attrs) =>
              attrs.indent ? { style: `margin-left: ${indentToMarginLeft(attrs.indent)}px` } : {},
          },
          // tiptap 純正 LineHeight はインライン span に付くため行間に効かない
          lineHeight: {
            default: null,
            parseHTML: (el) => el.style.lineHeight || null,
            renderHTML: (attrs) => (attrs.lineHeight ? { style: `line-height: ${attrs.lineHeight}` } : {}),
          },
        },
      },
    ];
  },
  addCommands() {
    const { types } = this.options;
    return {
      setBlockTextAlign:
        (align) =>
        ({ commands }) =>
          types.map((type) => commands.updateAttributes(type, { textAlign: align })).some(Boolean),
      setBlockLineHeight:
        (lineHeight) =>
        ({ commands }) =>
          types.map((type) => commands.updateAttributes(type, { lineHeight })).some(Boolean),
      // 共有の tr を直接変更する。setNodeMarkup は doc サイズを変えないので走査位置は有効なまま
      changeBlockIndent:
        (delta) =>
        ({ state, tr }) => {
          const { from, to } = state.selection;
          let changed = false;
          state.doc.nodesBetween(from, to, (node, pos) => {
            if (!types.includes(node.type.name)) return;
            const current = Number(node.attrs.indent) || 0;
            const next = clampIndent(current, delta);
            if (next !== current) {
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
              changed = true;
            }
          });
          return changed;
        },
    };
  },
});

/**
 * YouTube 埋め込み iframe を schema に載せるための atom ノード
 * 無いと ProseMirror が iframe を schema 外として除去する。YouTube 以外は sanitize 側で落ちる
 */
export const YoutubeIframe = Node.create({
  name: 'iframe',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      src: { default: null },
      frameborder: { default: '0' },
      allow: { default: null },
      allowfullscreen: { default: 'true' },
      width: { default: null },
      height: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: 'iframe' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['iframe', mergeAttributes(HTMLAttributes)];
  },
});
